import MuiButton from 'components/common/button/MuiButton';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { icon_magnifier, icon_invoice_list, icon_trash } from 'components/base/images';
import CustomSpan from 'components/common/text/CustomSpan';
import MuiWrapper from 'components/common/input/MuiWrapper';
import { IconButton, TextField } from '@material-ui/core';
import CustomDatePicker from 'components/common/input/CustomDatePicker';
import cx from 'classnames';
import { color } from 'styles/utils';
import EyeIcon from 'components/base/icons/EyeIcon';
import T from 'components/common/text/T';
import { useTranslation } from 'react-i18next';
import { InvoicePartner } from 'lib/types/invoiceTypes';
import AsyncFetchSelect from 'components/common/select/AsyncFetchSelect';
import { DebouncedFunc } from 'lodash';
import { OffsetPagingData } from 'lib/types/sharedTypes';
import { addCommas } from 'lib/library';
import moment from 'moment';
import useInput from 'lib/hooks/useInput';
import CustomPagination from 'components/common/pagination/CustomPagination';
import { PageSubTitle } from 'components/base/title/PageTitle';
import { useShallowAppSelector } from 'store/hooks';
import ListEmpty from 'components/common/list/ListEmpty';

interface Invoice {
  currency: string;
  endDate: number;
  enrollDate: number;
  invoiceCode: string;
  maxEndDate: number;
  minStartDate: number;
  partnerName: string;
  startDate: number;
  total: number;
}

interface InvoiceListProps {
  // data
  invoices: Invoice[];
  invoicesPagingData: OffsetPagingData;
  invoicePartners: InvoicePartner[];
  hasMoreInvoicePartners: boolean;
  onFetchInvoicePartners: DebouncedFunc<(first?: boolean | undefined) => Promise<void>>;
  fetchInvoicePartnersLoading: boolean;
  onSearchInvoicePartners: DebouncedFunc<(value: string) => Promise<void>>;
  // state
  isLoadSuccess: boolean;
  isLoadFailure: boolean;
  page: { value: number; onChange: (e: any) => void; setValue: (value: number) => void };
  period: { value: Date | null; onChange: (e: any) => void; setValue: (value: Date) => void };
  partner: { value: number; onChange: (e: any) => void; setValue: (value: number) => void };
  keyword: { value: string; onChange: (e: any) => void; setValue: (value: string) => void };
  onSearch: () => void;
  onChangePage: (value: number) => void;
  onShowInvoiceView: (value: string) => void;
  onOpenCreateInvoiceModal: () => void;
  onDeleteInvoice: (value: string) => void;
  onRefreshList: () => void;
}

function InvoiceList({
  // data
  invoices,
  invoicesPagingData,
  invoicePartners,
  hasMoreInvoicePartners,
  onFetchInvoicePartners,
  fetchInvoicePartnersLoading,
  onSearchInvoicePartners,
  // state
  isLoadSuccess,
  isLoadFailure,
  page,
  period,
  partner,
  keyword,
  onSearch,
  onChangePage,
  onShowInvoiceView,
  onOpenCreateInvoiceModal,
  onDeleteInvoice,
  onRefreshList,
}: InvoiceListProps) {
  const { invoicesPending } = useShallowAppSelector(state => ({
    invoicesPending: state.invoice.invoices.pending,
  }));

  const { t } = useTranslation();
  const checkedInvoiceView = useInput('');

  const handleTogglePdf = (id: string) => {
    if (checkedInvoiceView.value === id) {
      checkedInvoiceView.setValue('');
      onShowInvoiceView('');
    } else {
      checkedInvoiceView.setValue(id);
      onShowInvoiceView(id);
    }
  };

  const handleClickInvoiceDelete = (id: string) => {
    if (checkedInvoiceView.value === id) {
      checkedInvoiceView.setValue('');
      onShowInvoiceView('');
    }
    onDeleteInvoice(id);
  };

  return (
    <StyledInvoiceList data-component-name="InvoiceList">
      <div className="invoiceList__title_container">
        <PageSubTitle title={t('TITLE_INVOICE_LIST')} />
        <MuiButton
          config={{
            width: 175,
          }}
          className="invoiceList__new_invoice_btn md inset-shadow-default border-radius-round"
          variant="contained"
          color="primary"
          onClick={onOpenCreateInvoiceModal}
        >
          <CustomSpan fontSize={13} fontWeight={400}>
            {'+ '} <T>GLOBAL_NEW_INVOICE</T>
          </CustomSpan>
        </MuiButton>
      </div>

      <div className="invoiceList__content_container">
        <div className="invoiceList__filter_box">
          <AsyncFetchSelect
            className="sm invoiceList__partner_select_box"
            inputProps={{
              placeholder: t('PARTNER_LIST'),
              selectedValue: partner.value,
              setSelectedValue: partner.setValue,
              data: invoicePartners,
              hasMoreData: hasMoreInvoicePartners,
              idKey: 'userGroupIdx',
              labelKey: 'name',
              onFetch: onFetchInvoicePartners,
              searchLoading: fetchInvoicePartnersLoading,
              onSearch: onSearchInvoicePartners,
            }}
          />

          <CustomDatePicker
            type="rangeDate"
            fullWidth
            borderColor="#B5B7C1"
            height={32}
            fontSize={12}
            value={period.value}
            onChange={period.onChange}
            className={cx({
              error: period.value !== null ? !period.value : false,
            })}
            isClient={true}
          />
          <MuiWrapper
            className="invoiceList__search_wrapper sm"
            config={{
              borderColor: 'transparent',
              activeBorderColor: 'transparent',
              hoverBorderColor: 'transparent',
            }}
          >
            <>
              <TextField
                rows={1}
                className="radius-md"
                variant="outlined"
                fullWidth
                placeholder={t('SEACH_FOR_PROJECT')}
                value={keyword.value || ''}
                onChange={keyword.onChange}
                onKeyPress={e => e.key === 'Enter' && onSearch()}
              />
              <button className="btn-reset invoiceList__search_btn" onClick={onSearch}>
                <img src={icon_magnifier} alt="search" />
              </button>
            </>
          </MuiWrapper>
        </div>

        <div className="invoiceList__table_container">
          <div className="invoiceList__table">
            <div className="invoiceList__table_header">
              <div className="invoiceList__table_row header">
                <div className="invoiceList__table_cell header">
                  <CustomSpan fontColor={color.gray_week}>No.</CustomSpan>
                </div>
                <div className="invoiceList__table_cell header">
                  <T>GLOBAL_PARTNER</T>
                </div>
                <div className="invoiceList__table_cell header">
                  <T>GLOBAL_PERIOD</T>
                </div>
                <div className="invoiceList__table_cell header">
                  <T>GLOBAL_SALES_PRICE</T>
                </div>
                <div className="invoiceList__table_cell header">
                  <T>GLOBAL_VIEW</T>
                </div>
                <div className="invoiceList__table_cell header"></div>
              </div>
            </div>

            <div className="invoiceList__table_body">
              {isLoadSuccess &&
                (invoices.length > 0 ? (
                  invoices.map((item, idx) => (
                    <div className="invoiceList__table_row" key={idx}>
                      <div className="invoiceList__table_cell">
                        <CustomSpan fontColor={color.gray_week}>
                          {(invoicesPagingData?.page - 1) * 10 + idx + 1}
                        </CustomSpan>
                      </div>
                      <div className="invoiceList__table_cell">{item.partnerName}</div>
                      <div className="invoiceList__table_cell">
                        {moment(item.startDate).format('YYYY/MM/DD')}
                        {' ~ '}
                        {moment(item.endDate).format('YYYY/MM/DD')}
                      </div>
                      <div className="invoiceList__table_cell">
                        &#65510; {addCommas(item.total)}
                      </div>
                      <div className="invoiceList__table_cell">
                        <IconButton onClick={() => handleTogglePdf(item.invoiceCode)}>
                          <EyeIcon
                            color={
                              checkedInvoiceView.value === item.invoiceCode
                                ? color.blue
                                : color.gray_week
                            }
                          />
                        </IconButton>
                        <IconButton
                          onClick={() => {
                            handleClickInvoiceDelete(item.invoiceCode);
                          }}
                        >
                          <img src={icon_trash} />
                        </IconButton>
                      </div>
                    </div>
                  ))
                ) : (
                  <ListEmpty text="GLOBAL_EMPTY_LIST_DATA" />
                ))}

              {!!isLoadFailure && (
                <ListEmpty
                  text="GLOBAL_LIST_ERROR"
                  isRefreshButton={true}
                  onRefresh={onRefreshList}
                />
              )}
            </div>
          </div>

          {!!isLoadSuccess && invoices.length > 0 && !!invoicesPagingData?.totalPage && (
            <CustomPagination
              totalPage={invoicesPagingData?.totalPage}
              currPage={page.value}
              onChange={onChangePage}
            />
          )}
        </div>
      </div>
    </StyledInvoiceList>
  );
}

const StyledInvoiceList = styled.div`
  padding: 50px 0 30px;
  height: 100%;
  background-color: white;
  border-radius: 15px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.16);

  .invoiceList__title_container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 30px;
  }

  .invoiceList__content_container {
    margin-top: 50px;
    padding: 0 30px;

    .invoiceList__filter_box {
      display: flex;
      justify-content: space-between;
      flex-wrap: wrap;

      .invoiceList__partner_select_box {
        width: 155px;
      }

      .datePicker__box {
        width: 225px;
        .ant-picker-input {
          width: 65px;
        }
      }

      .invoiceList__search_wrapper {
        position: relative;
        width: 330px;
        .MuiInputBase-root {
          background-color: #f4f5fa;
        }
        .invoiceList__search_btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          position: absolute;
          right: 0;
          top: 0;
          width: 32px;
          height: 32px;
          border-top-right-radius: 5px;
          border-bottom-right-radius: 5px;
          background-color: ${color.navy_blue};
          img {
            width: 18px;
          }
        }
      }
    }

    .invoiceList__table_container {
      margin-top: 20px;

      .muiPagination {
        margin-top: 30px;
      }

      .invoiceList__table {
        width: 100%;
        table-layout: fixed;
        .invoiceList__table_row {
          display: flex;
          align-items: center;
        }
        .invoiceList__table_cell {
          &:nth-child(1) {
            width: 10%;
          }
          &:nth-child(2) {
            width: 25%;
          }
          &:nth-child(3) {
            width: 25%;
          }
          &:nth-child(4) {
            width: 25%;
          }
          &:nth-child(5) {
            width: 15%;
          }
        }

        .invoiceList__table_row {
          padding: 0 10px;
          vertical-align: middle;
          text-align: center;
          .invoiceList__table_cell {
            &:nth-child(2) {
              text-align: left;
              text-overflow: ellipsis;
              white-space: nowrap;
              overflow: hidden;
            }
            &:nth-child(3) {
              text-align: left;
            }
            &:nth-child(4) {
              text-align: right;
            }
          }
          &.header {
            background-color: #f4f5fa;
            font-size: 15px;
            font-weight: 500;

            &:first-child {
              border-top-left-radius: 5px;
              border-bottom-left-radius: 5px;
            }
            &:last-child {
              border-top-right-radius: 5px;
              border-bottom-right-radius: 5px;
            }
          }
        }

        .invoiceList__table_header {
          .invoiceList__table_row {
            height: 40px;
          }
        }
        .invoiceList__table_body {
          position: relative;
          width: 100%;
          height: 720px;
          margin-bottom: 20px;
          .invoiceList__table_row {
            height: 60px;
            font-size: 13px;
          }
        }
      }
    }
  }
`;

export default React.memo(InvoiceList);
